import { useState } from "react";
import { T } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Card, Btn } from "../ui/index.jsx";

export default function ProfilePage({ user, onBack, goPricing, currentPlan }) {
  const planNames = { free: "Free", starter: "Starter", core: "Core", pro: "Pro" };
  const planColors = { free: T.textDim, starter: T.blue, core: T.accent, pro: T.yellow };

  const isPaid = currentPlan && currentPlan !== "free";

  // 구독 해지 상태
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [reason, setReason] = useState("");

  const handleCancel = async () => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      const token = localStorage.getItem("phase_token");
      await api(
        "/subscription/cancel",
        { method: "POST", body: JSON.stringify({ reason: reason || null }) },
        token
      );
      setCancelDone(true);
    } catch (e) {
      setCancelError(e?.message || "해지 처리 중 문제가 발생했어요. 잠시 후 다시 시도해주세요.");
    }
    setCancelLoading(false);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <Btn variant="ghost" style={{ padding: "6px 12px" }} onClick={onBack}>←</Btn>
        <div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>내 계정</div>
      </div>

      {/* Account Info */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>계정 정보</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: T.textMuted }}>이메일</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{user?.email}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: T.textMuted }}>가입일</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{user?.created_at ? new Date(user.created_at).toLocaleDateString("ko-KR") : "-"}</span>
          </div>
        </div>
      </Card>

      {/* Plan Info */}
      <Card style={{ marginBottom: 16, borderLeft: `3px solid ${planColors[currentPlan]}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>현재 플랜</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, color: planColors[currentPlan] }}>{planNames[currentPlan]}</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
              {currentPlan === "free" ? "무료 플랜 사용 중" : "유료 플랜 이용 중"}
            </div>
          </div>
          <Btn variant={currentPlan === "free" ? "primary" : "ghost"} onClick={goPricing}>
            {currentPlan === "free" ? "업그레이드" : "플랜 변경"}
          </Btn>
        </div>

        {/* 구독 해지 - 유료 플랜일 때만 */}
        {isPaid && !cancelDone && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            {!cancelOpen ? (
              <button
                onClick={() => setCancelOpen(true)}
                style={{
                  background: "none", border: "none", padding: 0, cursor: "pointer",
                  fontSize: 12, color: T.textMuted, textDecoration: "underline",
                  textUnderlineOffset: 3, fontFamily: "inherit",
                }}
              >
                구독 해지
              </button>
            ) : (
              <div>
                <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 8 }}>
                  정말 구독을 해지하시겠어요?
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
                  해지해도 이번 결제 기간이 끝날 때까지는 현재 플랜을 그대로 이용할 수 있어요.
                  다음 결제는 진행되지 않습니다.
                </div>

                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="해지 이유를 남겨주시면 개선에 참고할게요 (선택)"
                  rows={2}
                  style={{
                    width: "100%", padding: "10px 12px", background: T.bg,
                    border: `1px solid ${T.border}`, borderRadius: 8,
                    color: T.text, fontSize: 13, outline: "none", boxSizing: "border-box",
                    resize: "none", fontFamily: "inherit", marginBottom: 12,
                  }}
                />

                {cancelError && (
                  <div style={{ fontSize: 12, color: T.red, marginBottom: 12 }}>{cancelError}</div>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                  <Btn
                    variant="ghost"
                    style={{ fontSize: 12, flex: 1 }}
                    onClick={() => { setCancelOpen(false); setCancelError(null); setReason(""); }}
                    disabled={cancelLoading}
                  >
                    유지하기
                  </Btn>
                  <Btn
                    variant="ghost"
                    style={{ fontSize: 12, flex: 1, borderColor: T.red + "40", color: T.red }}
                    onClick={handleCancel}
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? "처리 중..." : "해지하기"}
                  </Btn>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 해지 완료 안내 */}
        {cancelDone && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 6 }}>
              구독 해지가 완료됐어요
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6 }}>
              이번 결제 기간까지는 현재 플랜을 그대로 이용할 수 있어요.
              기간이 끝나면 Free 플랜으로 전환됩니다.
            </div>
          </div>
        )}
      </Card>

      {/* Usage Stats */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>이번 달 사용량</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ padding: 12, background: T.bg, borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: T.textMuted }}>종목 Q&A</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.accent }}>- / {currentPlan === "free" ? "5" : currentPlan === "starter" ? "20" : "∞"}회</div>
          </div>
          <div style={{ padding: 12, background: T.bg, borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: T.textMuted }}>시장 Q&A</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.cyan }}>- / {currentPlan === "free" ? "0" : currentPlan === "starter" ? "5" : "∞"}회</div>
          </div>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14, color: T.textMuted }}>계정 관리</div>
        <Btn variant="ghost" style={{ borderColor: T.border, color: T.textDim, fontSize: 12 }} onClick={() => alert("계정 삭제는 support@pause.ai.kr로 문의해주세요.")}>계정 삭제 요청</Btn>
      </Card>
    </div>
  );
}
